import {
  CALENDARIO_JANELA_MAXIMA_DIAS,
  type AgendamentoNaLista,
  type DiaDoCalendario,
} from '@baiocchi/shared'
import {
  Botao,
  CabecalhoCartao,
  Campo,
  Cartao,
  EstadoVazio,
  Esqueleto,
  Etiqueta,
  RodapePaginacao,
  Selecao,
  TabelaDados,
  cn,
  type ColunaTabela,
} from '@baiocchi/ui'
import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { agendamentosApi } from '../../api/agendamentos'
import { chaves } from '../../api/chaves'
import { useSessao } from '../../autenticacao/contexto-sessao'
import { EstadoDeErro } from '../../componentes/estados-tela'
import { EtiquetaStatusAgendamento } from '../../componentes/etiquetas-dominio'
import { IconeMais, IconeSeta, IconeVoltar } from '../../componentes/icones'
import {
  opcoesDeCatalogo,
  opcoesDeReferencia,
  useCatalogoAtivo,
  useEquipes,
} from '../../ganchos/usar-catalogos'
import { useFiltrosUrl } from '../../ganchos/usar-filtros-url'
import { usePaginacaoCursor } from '../../ganchos/usar-paginacao-cursor'
import { CabecalhoPagina } from '../../layout/layout-app'
import {
  formatarDataHora,
  formatarTelefone,
  hojeFiltro,
  paraDataFiltro,
} from '../../utilitarios/formatar'
import { GavetaAgendamento, ModalCriarAgendamento } from './acoes-agendamento'
import { GradeCalendario } from './grade-calendario'

type Visao = 'semana' | 'dia' | 'lista'

/**
 * TELA-08 — Agendamentos.
 *
 * Três visões: **Semana** e **Dia** (a grade do legado) e **Lista** — acréscimo
 * exigido pela spec, e a única visão realmente usável no celular, onde uma grade de
 * sete colunas por doze faixas horárias não cabe.
 *
 * A janela do calendário é limitada a {@link CALENDARIO_JANELA_MAXIMA_DIAS} dias no
 * servidor: a grade não é paginada porque precisa de todos os eventos da janela para
 * posicioná-los, e o limite é o contorno legítimo do "toda listagem é paginada".
 */
export function PaginaAgendamentos() {
  const { filtros, definir } = useFiltrosUrl()
  const { pode } = useSessao()

  const [visao, setVisao] = useState<Visao>('semana')
  const [ancora, setAncora] = useState(() => new Date())
  const [selecionado, setSelecionado] = useState<AgendamentoNaLista | null>(null)
  const [criando, setCriando] = useState(false)
  const [horaSugerida, setHoraSugerida] = useState<string | undefined>(undefined)

  const equipes = useEquipes()
  const motivos = useCatalogoAtivo('motivos-agendamento')

  const janela = useMemo(() => {
    if (visao === 'dia') {
      const dia = paraDataFiltro(ancora)
      return { de: dia, ate: dia }
    }
    // Semana começando no domingo, como a grade do legado.
    const inicio = new Date(ancora)
    inicio.setDate(inicio.getDate() - inicio.getDay())
    const fim = new Date(inicio)
    fim.setDate(fim.getDate() + 6)
    return { de: paraDataFiltro(inicio), ate: paraDataFiltro(fim) }
  }, [ancora, visao])

  const filtrosComuns = {
    equipeId: filtros.equipeId,
    responsavelUuid: filtros.responsavelUuid,
    motivoId: filtros.motivoId,
  }

  const calendario = useQuery({
    queryKey: chaves.agendamentos.calendario({ ...janela, ...filtrosComuns }),
    queryFn: ({ signal }) => agendamentosApi.calendario({ ...janela, ...filtrosComuns }, signal),
    enabled: visao !== 'lista',
    staleTime: 30_000,
  })

  const podeGerenciar = pode('AGENDAMENTO_GERENCIAR')
  const podeCriar = pode('AGENDAMENTO_CRIAR')

  /**
   * A API devolve **só os dias com compromisso** — o que é correto para um payload
   * de dados, mas não é uma grade: sem completar a janela, uma semana com um único
   * evento renderizaria uma coluna só, e o usuário não teria onde clicar para
   * agendar nos outros dias.
   *
   * Completar aqui, e não pedir ao servidor, é a divisão certa: quais dias a tela
   * mostra é decisão de **layout** (semana, dia, e um dia a visão de mês), e o
   * servidor não precisa saber disso.
   */
  const diasDaGrade = useMemo<ReadonlyArray<DiaDoCalendario>>(() => {
    const porDia = new Map((calendario.data?.dias ?? []).map((dia) => [dia.dia, dia]))
    const resultado: DiaDoCalendario[] = []

    // Percorre o dia civil, e não o instante: somar 24 h escorregaria no horário
    // de verão, se ele voltar.
    for (let cursor = new Date(`${janela.de}T12:00:00Z`); ; cursor.setDate(cursor.getDate() + 1)) {
      const chave = paraDataFiltro(cursor)
      resultado.push(porDia.get(chave) ?? { dia: chave, total: 0, agendamentos: [] })
      if (chave >= janela.ate || resultado.length > CALENDARIO_JANELA_MAXIMA_DIAS) break
    }

    return resultado
  }, [calendario.data, janela])

  return (
    <>
      <CabecalhoPagina
        titulo="Agendamentos"
        descricao="Compromissos vinculados aos leads. Toda ação aqui escreve na timeline do lead."
        acoes={
          podeCriar ? (
            <Botao
              onClick={() => {
                setHoraSugerida(undefined)
                setCriando(true)
              }}
              iconeEsquerda={<IconeMais className="h-4 w-4" />}
            >
              Novo agendamento
            </Botao>
          ) : null
        }
      />

      <Cartao className="mb-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <Campo rotulo="Equipe">
            {(atributos) => (
              <Selecao
                {...atributos}
                vazio="Todas as equipes"
                value={filtros.equipeId ?? ''}
                opcoes={opcoesDeReferencia(equipes.data?.dados)}
                onChange={(evento) => definir({ equipeId: evento.target.value || undefined })}
              />
            )}
          </Campo>

          <Campo rotulo="Motivo">
            {(atributos) => (
              <Selecao
                {...atributos}
                vazio="Todos os motivos"
                value={filtros.motivoId ?? ''}
                opcoes={opcoesDeCatalogo(motivos.data?.dados)}
                onChange={(evento) => definir({ motivoId: evento.target.value || undefined })}
              />
            )}
          </Campo>

          <Campo rotulo="Visão">
            {(atributos) => (
              <Selecao
                {...atributos}
                value={visao}
                opcoes={[
                  { valor: 'semana', rotulo: 'Semana' },
                  { valor: 'dia', rotulo: 'Dia' },
                  { valor: 'lista', rotulo: 'Lista' },
                ]}
                onChange={(evento) => setVisao(evento.target.value as Visao)}
              />
            )}
          </Campo>
        </div>
      </Cartao>

      {visao === 'lista' ? (
        <ListaAgendamentos filtros={filtrosComuns} onSelecionar={setSelecionado} />
      ) : (
        <Cartao semPadding>
          <CabecalhoCartao
            titulo={tituloDaJanela(janela, visao)}
            acoes={
              <div className="flex items-center gap-1">
                <Botao
                  variante="contorno"
                  tamanho="icone"
                  aria-label="Período anterior"
                  onClick={() => setAncora(deslocar(ancora, visao === 'dia' ? -1 : -7))}
                >
                  <IconeVoltar className="h-4 w-4" />
                </Botao>
                <Botao variante="contorno" tamanho="sm" onClick={() => setAncora(new Date())}>
                  Hoje
                </Botao>
                <Botao
                  variante="contorno"
                  tamanho="icone"
                  aria-label="Período seguinte"
                  onClick={() => setAncora(deslocar(ancora, visao === 'dia' ? 1 : 7))}
                >
                  <IconeSeta className="h-4 w-4" />
                </Botao>
              </div>
            }
          />

          {calendario.isPending ? (
            <div className="space-y-2 p-4">
              {Array.from({ length: 8 }, (_, indice) => (
                <Esqueleto key={indice} className="h-10 w-full" />
              ))}
            </div>
          ) : calendario.isError ? (
            <EstadoDeErro
              erro={calendario.error}
              aoTentarNovamente={() => void calendario.refetch()}
            />
          ) : (
            <>
              <GradeCalendario
                dias={diasDaGrade}
                hoje={hojeFiltro()}
                podeCriar={podeCriar}
                onSelecionarEvento={setSelecionado}
                onSelecionarVago={(dia, hora) => {
                  setHoraSugerida(`${dia}T${String(hora).padStart(2, '0')}:00`)
                  setCriando(true)
                }}
              />

              {/* Legenda por motivo — a cor vem do cadastro (TELA-12). */}
              <div className="flex flex-wrap gap-2 border-t border-borda bg-superficie-2 px-4 py-3">
                {(motivos.data?.dados ?? []).map((motivo) => (
                  <Etiqueta key={motivo.id} tom="neutro" cor={motivo.cor ?? undefined}>
                    {motivo.titulo}
                  </Etiqueta>
                ))}
                {(motivos.data?.dados ?? []).length === 0 && (
                  <p className="text-xs text-conteudo-fraco">
                    A legenda usa as cores cadastradas em Configurações → Motivos de agendamento.
                  </p>
                )}
              </div>
            </>
          )}
        </Cartao>
      )}

      <GavetaAgendamento
        agendamento={selecionado}
        onFechar={() => setSelecionado(null)}
        podeGerenciar={podeGerenciar}
      />

      <ModalCriarAgendamento
        aberto={criando}
        iniciaEmSugerido={horaSugerida}
        onFechar={() => {
          setCriando(false)
          setHoraSugerida(undefined)
        }}
      />
    </>
  )
}

/** Visão de lista — [NOVA] em relação ao legado, e a que funciona no celular. */
function ListaAgendamentos({
  filtros,
  onSelecionar,
}: {
  filtros: Record<string, string | undefined>
  onSelecionar: (agendamento: AgendamentoNaLista) => void
}) {
  const paginacao = usePaginacaoCursor(50)
  const [apenasVencidos, setApenasVencidos] = useState(false)

  const consultaFiltros = {
    ...filtros,
    limite: paginacao.limite,
    cursor: paginacao.cursor,
    ordenar: 'iniciaEm',
    ...(apenasVencidos ? { vencidos: 'true' } : {}),
  }

  const consulta = useQuery({
    queryKey: chaves.agendamentos.lista(consultaFiltros),
    queryFn: ({ signal }) => agendamentosApi.listar(consultaFiltros, signal),
    staleTime: 30_000,
  })

  const colunas: ReadonlyArray<ColunaTabela<AgendamentoNaLista>> = [
    {
      chave: 'iniciaEm',
      cabecalho: 'Quando',
      ordenavel: true,
      classe: 'whitespace-nowrap',
      celula: (item) => (
        <span
          className={cn(
            'text-xs tabular-nums',
            item.vencido ? 'font-semibold text-perigo' : 'text-conteudo-suave',
          )}
        >
          {formatarDataHora(item.iniciaEm)}
        </span>
      ),
    },
    {
      chave: 'lead',
      cabecalho: 'Lead',
      celula: (item) => (
        <div className="flex min-w-0 flex-col">
          <Link
            to={`/leads/${item.lead.uuid}`}
            onClick={(evento) => evento.stopPropagation()}
            className="truncate font-medium text-marca-600 hover:underline"
          >
            {item.lead.clienteNome}
          </Link>
          <span className="truncate text-xs tabular-nums text-conteudo-fraco">
            {formatarTelefone(item.lead.clienteContato)}
          </span>
        </div>
      ),
    },
    {
      chave: 'motivo',
      cabecalho: 'Motivo',
      celula: (item) => (
        <Etiqueta tom="neutro" cor={item.motivo.cor}>
          {item.motivo.titulo}
        </Etiqueta>
      ),
    },
    {
      chave: 'responsavel',
      cabecalho: 'Responsável',
      ocultarAte: 'md',
      celula: (item) => <span className="text-xs">{item.responsavel.nome}</span>,
    },
    {
      chave: 'equipe',
      cabecalho: 'Equipe',
      ocultarAte: 'lg',
      celula: (item) => <span className="text-xs">{item.equipe?.titulo ?? '—'}</span>,
    },
    {
      chave: 'status',
      cabecalho: 'Situação',
      ordenavel: true,
      celula: (item) => (
        <div className="flex flex-wrap items-center gap-1">
          <EtiquetaStatusAgendamento status={item.status} />
          {item.vencido ? <Etiqueta tom="perigo">Vencido</Etiqueta> : null}
        </div>
      ),
    },
  ]

  const itens = consulta.data?.dados ?? []

  return (
    <Cartao semPadding>
      <CabecalhoCartao
        titulo="Lista de compromissos"
        acoes={
          <button
            type="button"
            aria-pressed={apenasVencidos}
            onClick={() => {
              setApenasVencidos((valor) => !valor)
              paginacao.reiniciar()
            }}
            className={cn(
              'rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
              apenasVencidos
                ? 'border-perigo bg-perigo text-white'
                : 'border-borda-forte bg-superficie text-conteudo-suave hover:bg-superficie-2',
            )}
          >
            Só vencidos
          </button>
        }
      />

      <TabelaDados
        colunas={colunas}
        itens={itens}
        chaveDe={(item) => item.uuid}
        carregando={consulta.isPending}
        erro={
          consulta.isError ? (
            <EstadoDeErro erro={consulta.error} aoTentarNovamente={() => void consulta.refetch()} />
          ) : undefined
        }
        realce={(item) => item.vencido}
        onClicarLinha={onSelecionar}
        vazio={
          <EstadoVazio
            titulo="Nenhum compromisso"
            descricao="Nada agendado para os filtros escolhidos."
          />
        }
        cartaoMobile={(item) => (
          <button
            type="button"
            onClick={() => onSelecionar(item)}
            className={cn(
              'flex w-full flex-col gap-1.5 rounded-lg border p-3 text-left',
              item.vencido ? 'border-perigo/30 bg-perigo-suave' : 'border-borda bg-superficie',
            )}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-semibold tabular-nums text-conteudo">
                {formatarDataHora(item.iniciaEm)}
              </span>
              <EtiquetaStatusAgendamento status={item.status} />
            </div>
            <span className="truncate font-medium text-conteudo">{item.lead.clienteNome}</span>
            <Etiqueta tom="neutro" cor={item.motivo.cor}>
              {item.motivo.titulo}
            </Etiqueta>
            <span className="text-xs text-conteudo-fraco">{item.responsavel.nome}</span>
          </button>
        )}
      />

      <RodapePaginacao
        limite={paginacao.limite}
        onAlterarLimite={paginacao.alterarLimite}
        quantidadeNaPagina={itens.length}
        indiceInicial={itens.length === 0 ? 0 : paginacao.indiceInicial}
        total={consulta.data?.paginacao.total ?? null}
        temAnterior={paginacao.temAnterior}
        temProximo={consulta.data?.paginacao.temMais ?? false}
        onAnterior={paginacao.voltar}
        onProximo={() => paginacao.avancar(consulta.data?.paginacao.proximoCursor ?? null)}
      />
    </Cartao>
  )
}

function deslocar(data: Date, dias: number): Date {
  const nova = new Date(data)
  nova.setDate(nova.getDate() + dias)
  return nova
}

function tituloDaJanela(janela: { de: string; ate: string }, visao: Visao): string {
  const formatador = new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
  const de = formatador.format(new Date(`${janela.de}T12:00:00Z`))
  if (visao === 'dia') return de
  const ate = formatador.format(new Date(`${janela.ate}T12:00:00Z`))
  return `${de} — ${ate}`
}

/** Reexportado para o teste do limite de janela. */
export { CALENDARIO_JANELA_MAXIMA_DIAS }
